import React, { useCallback, useEffect, useState } from 'react';

import { Plus } from 'lucide-react';

import { apiGet } from '../../lib/api_v2';
import { logError } from '../../lib/logger';
import { openWidgetsFromOnboarding } from '../../lib/onboardingCardActions';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { useWidgetTemplateActions } from '../../hooks/useWidgetTemplateActions';
import { OnboardingCard, OnboardingCardGrid, OnboardingCardTile } from './OnboardingCard';
import { templateIconUrl } from './WidgetIcon';

// Three templates plus the "browse everything" tile fills the card's 4-up row.
const TEMPLATE_COUNT = 3;

interface WidgetTemplate {
  name?: string | null;
  slug: string;
}

interface AddWidgetCardProps {
  className?: string;
  onDismiss: () => void;
  workspaceId?: string | null;
}

function AddWidgetCard({ className, onDismiss, workspaceId }: AddWidgetCardProps) {
  const { mainChannelId, profile } = useRouteWorkspace();
  const [templates, setTemplates] = useState<WidgetTemplate[]>([]);

  // Same hook the gallery installs through, so a widget added here lands in the drawer
  // exactly as it would from the gallery — including the workspace/channel bookkeeping.
  const { installTemplate, canInstallTemplate } = useWidgetTemplateActions({
    canUseWidgetTemplates: Boolean(profile),
    workspaceId,
    channelId: mainChannelId,
    setTemplates,
    source: 'onboarding-card',
    onError: (message: string) => logError('AddWidgetCard', 'Failed to install widget template', { message }),
  });

  useEffect(
    function loadWidgetTemplates() {
      if (!workspaceId) return undefined;

      const abortController = new AbortController();

      async function fetchTemplates() {
        try {
          const query = new URLSearchParams({ workspace_id: String(workspaceId) });
          const response = await apiGet(`/widget_templates?${query.toString()}`, { signal: abortController.signal });
          const data = await response.json();

          if (abortController.signal.aborted) return;
          if (!response.ok || !data?.ok || !Array.isArray(data.data?.widget_templates)) return;

          setTemplates(data.data.widget_templates.slice(0, TEMPLATE_COUNT));
        } catch (error) {
          if (abortController.signal.aborted) return;
          logError('AddWidgetCard', 'Failed to load widget templates', { error });
        }
      }

      fetchTemplates();

      return () => abortController.abort();
    },
    [workspaceId],
  );

  const handleInstall = useCallback(
    async (template: WidgetTemplate) => {
      await installTemplate(template);
      // Open the drawer so the widget that just landed is actually visible.
      openWidgetsFromOnboarding();
      onDismiss();
    },
    [installTemplate, onDismiss],
  );

  const handleBrowse = useCallback(() => {
    openWidgetsFromOnboarding();
    onDismiss();
  }, [onDismiss]);

  return (
    <OnboardingCard className={className} dismissLabel="Dismiss add widget" onDismiss={onDismiss} title="Add widget">
      <OnboardingCardGrid>
        {templates.map((template) => {
          const iconUrl = templateIconUrl(template);

          return (
            <OnboardingCardTile
              key={template.slug}
              accessibleLabel={`Add ${template.name || 'widget'}`}
              className="bg-gray-50 hover:bg-white hover:brightness-100 dark:bg-neutral-800 dark:hover:bg-neutral-700"
              disabled={!canInstallTemplate(template, { isLoading: false })}
              onClick={() => handleInstall(template)}
            >
              {/* Icon and name are one centred unit; px-2 keeps a long name from running
                  into the tile edge before it truncates, same as the suggested-people names. */}
              <span className="flex w-full min-w-0 flex-col items-center gap-1 px-3">
                {iconUrl ? <img src={iconUrl} alt="" className="size-7 rounded-md" draggable="false" /> : null}
                <span className="w-full min-w-0 truncate text-center text-xs/5 font-medium text-black dark:text-neutral-100">
                  {template.name}
                </span>
              </span>
            </OnboardingCardTile>
          );
        })}
        <OnboardingCardTile accessibleLabel="Browse widgets" onClick={handleBrowse}>
          <Plus size={18} strokeWidth={1.5} absoluteStrokeWidth />
        </OnboardingCardTile>
      </OnboardingCardGrid>
    </OnboardingCard>
  );
}

export default AddWidgetCard;
